import { Injectable, isDevMode } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { shareReplay, tap, catchError } from "rxjs/operators";

interface CacheContent {
  expiry: number;
  value: any;
}

@Injectable({
  providedIn: "root",
})
export class CacheService {
  // We store the actual Observables. This prevents "In-Flight" logic bugs.
  private cache = new Map<string, Observable<any>>();
  readonly DEFAULT_MAX_AGE: number = 300000;

  get<T = any>(
    key: string,
    fallback?: Observable<any>,
    maxAge: number = this.DEFAULT_MAX_AGE,
    forceRefresh = false
  ): Observable<T> {
    if (forceRefresh) {
      this.delete(key);
    }

    // Check for cached observable
    const cached = this.cache.get(key);
    if (cached) {
      if (isDevMode()) {
        console.log(`%cReturning cached observable for ${key}`, "color: green");
      }
      return cached;
    }

    // If no cache and no fallback, we can't do anything
    if (!fallback) {
      return throwError(
        () => new Error(`No cache for ${key} and no fallback provided`)
      );
    }

    if (isDevMode()) {
      console.log(`%cCalling API for ${key}`, "color: purple");
    }

    const request = fallback.pipe(
      // shareReplay(1) caches the result
      // If 10 components subscribe now, only 1 API call happens
      // If a component subscribes LATER, it gets the last cached value instantly
      shareReplay(1),
      tap(() => {
        // Schedule deletion after maxAge
        setTimeout(() => this.delete(key), maxAge);
      }),
      catchError((err) => {
        // If the API fails, don't cache the error; allow a retry next time.
        this.delete(key);
        return throwError(() => err);
      })
    );

    this.cache.set(key, request);
    return request;
  }

  delete(key: string) {
    this.cache.delete(key);
  }

  deleteAll() {
    this.cache.clear();
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }
}
